import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  Collapse,
  useTheme,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as InProgressIcon,
  Analytics as AnalysisIcon,
  Home as PropertyIcon,
  LocationOn as LocationIcon,
  TrendingUp as PricingIcon,
  Balance as NegotiationIcon,
  Architecture as FloorPlanIcon,
  LocationCity as NeighborhoodIcon,
  Description as ListingIcon,
  People as ConsultationIcon,
  Assessment as LeadIcon,
  Assignment as TransactionIcon,
  Tour as TourIcon,
  PersonSearch as MatchIcon,
  Psychology as SentimentIcon,
  Hub as OrchestratorIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppSelector, selectActiveAnalysis, selectAnalysisProgress } from '../../store';
import { AnalysisToolType, AnalysisStatus } from '../../types/property';

interface LiveActivityFeedProps {
  onClose: () => void;
}

interface ActivityLog {
  id: string;
  tool: AnalysisToolType;
  status: AnalysisStatus;
  message: string;
  timestamp: Date;
  progress?: number;
}

const LiveActivityFeed: React.FC<LiveActivityFeedProps> = ({ onClose }) => {
  const theme = useTheme();
  const activeAnalysis = useAppSelector(selectActiveAnalysis);
  const progress = useAppSelector(selectAnalysisProgress);
  
  const [isExpanded, setIsExpanded] = useState(true);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);

  // Tool configuration with icons and descriptions
  const toolConfig: Record<AnalysisToolType, { 
    label: string; 
    icon: React.ReactNode; 
    color: string;
    description: string;
  }> = {
    propertyParser: {
      label: 'Property Parser',
      icon: <PropertyIcon fontSize="small" />,
      color: theme.palette.primary.main,
      description: 'Extracting property details and specifications',
    },
    floorPlan: {
      label: 'Floor Plan Analysis',
      icon: <FloorPlanIcon fontSize="small" />,
      color: theme.palette.secondary.main,
      description: 'Analyzing layout and space utilization',
    },
    cma: {
      label: 'Market Analysis',
      icon: <AnalysisIcon fontSize="small" />,
      color: theme.palette.success.main,
      description: 'Comparing market values and trends',
    },
    neighborhood: {
      label: 'Neighborhood Intelligence',
      icon: <NeighborhoodIcon fontSize="small" />,
      color: theme.palette.info.main,
      description: 'Gathering local area insights',
    },
    pricing: {
      label: 'Pricing Strategy',
      icon: <PricingIcon fontSize="small" />,
      color: theme.palette.warning.main,
      description: 'Calculating optimal pricing',
    },
    negotiation: {
      label: 'Negotiation Support',
      icon: <NegotiationIcon fontSize="small" />,
      color: theme.palette.error.main,
      description: 'Developing negotiation strategies',
    },
    listingDescription: {
      label: 'Listing Description',
      icon: <ListingIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Creating compelling listing copy',
    },
    clientConsultation: {
      label: 'Client Consultation',
      icon: <ConsultationIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Preparing consultation materials',
    },
    leadQualification: {
      label: 'Lead Qualification',
      icon: <LeadIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Qualifying potential leads',
    },
    transactionManagement: {
      label: 'Transaction Management',
      icon: <TransactionIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Managing transaction workflow',
    },
    preTourPrep: {
      label: 'Pre-Tour Prep',
      icon: <TourIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Preparing for property tours',
    },
    clientMatch: {
      label: 'Client Matching',
      icon: <MatchIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Matching clients to properties',
    },
    buyerSentiment: {
      label: 'Buyer Sentiment',
      icon: <SentimentIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Analyzing buyer psychology',
    },
    agentOrchestrator: {
      label: 'Agent Orchestrator',
      icon: <OrchestratorIcon fontSize="small" />,
      color: theme.palette.grey[600],
      description: 'Coordinating agent workflows',
    },
  };

  // Generate status messages based on tool progress
  const generateStatusMessage = (tool: AnalysisToolType, status: AnalysisStatus, toolProgress?: number) => {
    const config = toolConfig[tool];
    
    switch (status) {
      case 'pending':
        return `${config.label} queued for analysis`;
      case 'in_progress':
        if (toolProgress !== undefined) {
          return `${config.label} analyzing... ${Math.round(toolProgress)}% complete`;
        }
        return `${config.label} is analyzing the property`;
      case 'completed':
        return `${config.label} completed successfully`;
      case 'error':
        return `${config.label} encountered an error`;
      default:
        return `${config.label} status unknown`;
    }
  };

  // Update activity logs when progress changes
  useEffect(() => {
    if (!activeAnalysis?.selectedTools) return;

    const newLogs: ActivityLog[] = [];
    
    activeAnalysis.selectedTools.forEach((tool) => {
      const toolProgress = progress[tool];
      if (toolProgress) {
        const message = generateStatusMessage(tool, toolProgress.status, toolProgress.progress);
        
        newLogs.push({
          id: `${tool}-${toolProgress.status}-${Date.now()}`,
          tool,
          status: toolProgress.status,
          message,
          timestamp: new Date(),
          progress: toolProgress.progress,
        });
      }
    });

    setActivityLogs(prev => {
      // Merge new logs with existing ones, avoiding duplicates
      const mergedLogs = [...prev];
      
      newLogs.forEach(newLog => {
        const existingIndex = mergedLogs.findIndex(log => 
          log.tool === newLog.tool && log.status === newLog.status
        );
        
        if (existingIndex >= 0) {
          // Update existing log
          mergedLogs[existingIndex] = newLog;
        } else {
          // Add new log
          mergedLogs.push(newLog);
        }
      });
      
      // Sort by timestamp (newest first) and limit to 50 entries
      return mergedLogs
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 50);
    });
  }, [progress, activeAnalysis]);

  // Get status icon
  const getStatusIcon = (status: AnalysisStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'in_progress':
        return <InProgressIcon color="primary" fontSize="small" />;
      default:
        return <PendingIcon color="disabled" fontSize="small" />;
    }
  };

  // Format timestamp
  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  // Count active and completed tools
  const activeCount = activityLogs.filter(log => log.status === 'in_progress').length;
  const completedCount = activityLogs.filter(log => log.status === 'completed').length;

  if (!activeAnalysis) {
    return null;
  }

  return (
    <Card
      sx={{
        width: 350,
        height: 'fit-content',
        maxHeight: 600,
        position: 'fixed',
        top: theme.spacing(10),
        right: theme.spacing(2),
        zIndex: theme.zIndex.modal - 2,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={activeCount} color="secondary">
              <AnalysisIcon />
            </Badge>
            <Typography variant="subtitle1" fontWeight="bold">
              Live Activity Feed
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${completedCount}/${activeAnalysis.selectedTools.length}`}
              size="small"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontSize: '0.7rem',
              }}
            />
            <IconButton 
              size="small" 
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ color: 'inherit' }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton 
              size="small" 
              onClick={onClose}
              sx={{ color: 'inherit' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Activity List */}
        <Collapse in={isExpanded}>
          <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
            <List sx={{ p: 0 }}>
              {activityLogs.map((log, index) => {
                const config = toolConfig[log.tool];
                
                return (
                  <React.Fragment key={log.id}>
                    <ListItem sx={{ py: 1.5, px: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box sx={{ position: 'relative' }}>
                          <Box sx={{ color: config.color }}>
                            {config.icon}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -2,
                              right: -2,
                              bgcolor: 'background.paper',
                              borderRadius: '50%',
                              p: 0.25,
                            }}
                          >
                            {getStatusIcon(log.status)}
                          </Box>
                        </Box>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={log.message}
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(log.timestamp)}
                            </Typography>
                            {log.status === 'in_progress' && log.progress !== undefined && (
                              <LinearProgress
                                variant="determinate"
                                value={log.progress}
                                sx={{
                                  mt: 0.5,
                                  height: 3,
                                  borderRadius: 1.5,
                                  backgroundColor: theme.palette.grey[200],
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 1.5,
                                    backgroundColor: config.color,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItem>
                    {index < activityLogs.length - 1 && <Divider variant="inset" />}
                  </React.Fragment>
                );
              })}
              
              {activityLogs.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No activity yet"
                    secondary="Analysis will begin shortly..."
                    sx={{ textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default LiveActivityFeed;